import { Components } from '@/api/generated/api';
import { Product } from '@/api/generated/api.enum';
import DigidooAvatar from '@/assets/DigidooAvatar.vue';
import GroupIcon from '@/assets/icons/GroupIcon.vue';
import UserIcon from '@/assets/icons/UserIcon.vue';
import { DEFAULT_CURRENCY } from 'common/constants/billing.constants';
import { isNotNullish } from 'common/utils/array.utils';
import { type BillingPrice, type ProductMarketPricing, type ProductUIConfig, type ProductWithMeta } from './types';

export const productOrder: Components.Schemas.Product[] = ['proUser', 'team', 'school'];
export const purchasableProductOrder: Components.Schemas.Product[] = ['team', 'school'];

export function isPurchasableProduct(product: Components.Schemas.Product): boolean {
  return product !== Product.proUser;
}

export function isProductAvailableForPurchase(product: ProductWithMeta | null | undefined): product is ProductWithMeta {
  return !!product && isPurchasableProduct(product.product) && product.pricing?.yearly != null;
}

export const productMetaByProduct: Record<Components.Schemas.Product, ProductUIConfig> = {
  proUser: {
    icon: UserIcon,
    titleKey: 'billing.products.pro.title',
    descriptionKey: 'billing.products.pro.description',
    pricingCalculatorDescriptionKey: 'billing.products.pro.pricingCalculatorDescription',
    highlightKey: 'billing.products.pro.highlight',
    highlightColor: 'text-warning',
    pricingLabelKey: 'billing.products.pro.pricing',
  },
  team: {
    icon: GroupIcon,
    titleKey: 'billing.products.team.title',
    descriptionKey: 'billing.products.team.description',
    pricingCalculatorDescriptionKey: 'billing.products.team.pricingCalculatorDescription',
    highlightKey: 'billing.products.team.highlight',
    highlightColor: 'text-warning',
    pricingLabelKey: 'billing.products.team.pricing',
  },
  school: {
    icon: DigidooAvatar,
    titleKey: 'billing.products.school.title',
    descriptionKey: 'billing.products.school.description',
    pricingCalculatorDescriptionKey: 'billing.products.school.pricingCalculatorDescription',
    highlightKey: 'billing.products.school.highlight',
    highlightColor: 'text-tint',
    pricingLabelKey: 'billing.products.school.pricing',
  },
};

export function getProductMeta(product: Components.Schemas.Product): ProductUIConfig | undefined {
  return productMetaByProduct[product];
}

/** Maps a catalog interval into the internal price shape (per-seat for seat products, flat otherwise). */
function toInternalPrice(
  interval: Components.Schemas.BillingIntervalPricingDto | null,
  supportsSeats: boolean,
): BillingPrice | null {
  if (!interval) return null;
  if (supportsSeats) {
    const seatTiers = interval.seatTiers ?? undefined;
    const lowestTierCents = seatTiers?.length ? Math.min(...seatTiers.map((tier) => tier.perSeatCents)) : undefined;
    return {
      amountCents: 0,
      // Tiered Stripe prices have no unit_amount. Preserve the historical display/fallback seam by
      // exposing their lowest tier as perSeatCents while keeping the full table for total math.
      perSeatCents: interval.unitAmountCents ?? lowestTierCents,
      seatTiers,
    };
  }
  return { amountCents: interval.unitAmountCents ?? 0 };
}

/** Maps one product row to its pricing in the currently resolved market. */
export function getProductMarketPricing(
  product: Components.Schemas.ProductTypeDto,
  marketPricing: Components.Schemas.BillingMarketPricingDtoData | null,
): ProductMarketPricing | null {
  const entry = marketPricing?.products.find((candidate) => candidate.productTypeId === product.id);
  if (!marketPricing || !entry?.yearly) return null;

  return {
    currency: marketPricing.currency,
    yearly: toInternalPrice(entry.yearly, product.supportsSeats),
    monthly: toInternalPrice(entry.monthly, product.supportsSeats),
  };
}

/**
 * The purchasable catalog in display order, merged with the market pricing from the Stripe catalog.
 * Products without UI meta OR without a yearly price in this market (no Stripe currency option — e.g.
 * One in Switzerland) are dropped: market availability is a Stripe-data decision, never a code branch.
 */
export function buildPurchasableProducts(
  rows: Components.Schemas.ProductTypeDto[],
  marketPricing: Components.Schemas.BillingMarketPricingDtoData | null,
): ProductWithMeta[] {
  return purchasableProductOrder
    .map((productKey) => rows.find((row) => row.product === productKey))
    .filter(isNotNullish)
    .map((row) => {
      const meta = getProductMeta(row.product);
      const pricing = getProductMarketPricing(row, marketPricing);
      if (!meta || !pricing) return null;
      return { ...row, meta, pricing, priceLabel: '' };
    })
    .filter(isNotNullish);
}

/** Whether the product bills monthly IN THIS MARKET (the Stripe price carries a monthly currency option). */
export function hasMonthlyBilling(product: ProductWithMeta): boolean {
  return product.pricing?.monthly != null;
}

/**
 * The shared market currency of a product list. All priced products carry the same currency (the
 * pricing payload is per-market), but not every product is priced in every market (e.g. One in
 * Switzerland has pricing null) — so the first PRICED product decides, never blindly index 0.
 */
export function getProductsMarketCurrency(products: ProductWithMeta[]): string {
  return products.find((product) => product.pricing != null)?.pricing?.currency ?? DEFAULT_CURRENCY;
}
